// Coach page
// - Validates `:coachId` and `?edit` locally
// - If `coachId === "new"` without ?edit => redirect to Admin list (old loader behavior)
// - Shows Spinner while Firebase users are loading
// - Renders <h1> error if coach is not found

import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CoachCard from '../components/CoachCard';
import Spinner from '../components/Spinner';
import { useUser } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';

export default function PageCoach() {
  const { coachId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { entity: found, isResolved } = useUser(coachId);

  // Handle "create new" route: /coaches/new
  useEffect(() => {
    // If user navigates to "new" without ?edit, redirect to the coaches list tab in Admin
    if (coachId === 'new') {
      const editParam = searchParams.get('edit');
      if (!editParam) navigate(-1, { replace: true });
    }
  }, [coachId, searchParams, navigate]);

  // Case: creating a new coach with ?edit present
  if (coachId === 'new') {
    const editParam = searchParams.get('edit');

    // If redirect just triggered (no ?edit), keep UI calm with a spinner
    if (!editParam) {
      return (
        <Layout screenSized className='page-coach'>
          <Spinner show />
        </Layout>
      );
    }

    // Render creation mode; pass minimal shape if your card expects an object
    return (
      <Layout screenSized className='page-coach' trail>
        <CoachCard coach={{ id: 'new' }} />
      </Layout>
    );
  }

  // Find an existing coach by id and role
  const coach = found?.role === UsersRoles.COACH ? found : undefined;

  // Spinner only while the record could still show up — the current user's own
  // card is already in usersById, so it renders without waiting for the list
  if (!coach && !isResolved) {
    return (
      <Layout screenSized className='page-coach'>
        <Spinner show />
      </Layout>
    );
  }

  // Not found => plain H1 error (matches your requirement)
  if (!coach) {
    return (
      <Layout screenSized className='page-coach'>
        <h1>{`Error: no route matches url "/coaches/${coachId}"`}</h1>
      </Layout>
    );
  }

  // Happy path
  return (
    <Layout className='page-coach' trail>
      <CoachCard coach={coach} />
    </Layout>
  );
}
